import { useState, type ReactNode } from "react"

import { Button, Textarea, toast } from "@coval/ui"
import { CircleCheck, Flag, Link, X } from "lucide-react"

import { useCopyToClipboard } from "@/hooks/useCopyToClipboard"

import type {
  InvestigationFinding,
  InvestigationFindingAction,
} from "../utils/investigationFindings"

const FINDING_NOTE_MAX_CHARS = 2000

interface FindingActionControlsProps {
  finding: InvestigationFinding
  findingHref: string
  // Rendered at the start of the button row, opposite the status actions, for a
  // caller that owns affordances belonging on this same line.
  leading?: ReactNode
  // Rendered as the first of the status actions, for a caller whose own primary
  // action belongs in that group rather than opposite it.
  primaryAction?: ReactNode
  // Resolves true only when the action applied: anything the user typed survives a
  // conflict or a failure.
  onAction: (
    finding: InvestigationFinding,
    action: InvestigationFindingAction,
    note?: string,
  ) => Promise<boolean>
}

// The acknowledge / resolve / dismiss / flag-wrong controls shared by the Home
// briefing row and the investigation history page. Visibility mirrors Sofia's
// authoritative _ACTION_ALLOWED_STATUSES. Dismissed findings can be reopened;
// resolved findings keep their immutable episode history.
export function FindingActionControls({
  finding,
  findingHref,
  leading,
  onAction,
  primaryAction,
}: FindingActionControlsProps) {
  const { copied, copy } = useCopyToClipboard()
  const [flagFormOpen, setFlagFormOpen] = useState(false)
  const [flagNote, setFlagNote] = useState("")
  const [resolveFormOpen, setResolveFormOpen] = useState(false)
  const [resolveNote, setResolveNote] = useState("")
  const [pendingAction, setPendingAction] =
    useState<InvestigationFindingAction | null>(null)

  const canChangeStatus =
    finding.status === "open" || finding.status === "acknowledged"
  const hasActions = canChangeStatus || finding.status === "dismissed"
  const runAction = async (
    action: InvestigationFindingAction,
    note?: string,
  ) => {
    setPendingAction(action)
    let applied = false
    try {
      applied = await onAction(finding, action, note)
    } finally {
      setPendingAction(null)
    }
    if (applied) {
      setFlagFormOpen(false)
      setFlagNote("")
      setResolveFormOpen(false)
      setResolveNote("")
    }
  }

  // Self-contained block: the button row wraps its own buttons and any open form takes
  // the full width beneath it, so callers can drop it into a list row or an actions slot
  // without managing the form's layout.
  return (
    <div className="w-full space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {leading && (
          <div className="flex flex-wrap items-center gap-2">{leading}</div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {primaryAction}
          <Button
            type="button"
            variant="secondary"
            disabled={!findingHref}
            onClick={() => {
              void copy(new URL(findingHref, window.location.origin).href).then(
                (success) => {
                  if (!success) {
                    toast.danger("Couldn't copy the finding link. Try again.")
                  }
                },
              )
            }}
          >
            <Link aria-hidden />
            {copied ? "Link copied" : "Copy link"}
          </Button>
          {finding.status === "dismissed" && (
            <Button
              type="button"
              variant="secondary"
              loading={pendingAction === "reopen"}
              disabled={pendingAction !== null}
              onClick={() => {
                void runAction("reopen")
              }}
            >
              Reopen
            </Button>
          )}
          {canChangeStatus && (
            // Acknowledging reports back in place: the button keeps its slot and
            // states the new status, so nothing beside it moves under the pointer
            // that just clicked it.
            <Button
              type="button"
              variant="secondary"
              loading={pendingAction === "acknowledge"}
              disabled={
                pendingAction !== null || finding.status === "acknowledged"
              }
              onClick={() => {
                void runAction("acknowledge")
              }}
            >
              {finding.status === "acknowledged"
                ? "Acknowledged"
                : "Acknowledge"}
            </Button>
          )}
          {canChangeStatus && (
            <Button
              type="button"
              icon
              tooltip="Dismiss"
              variant="secondary"
              loading={pendingAction === "dismiss"}
              disabled={pendingAction !== null}
              onClick={() => {
                void runAction("dismiss")
              }}
            >
              <X aria-hidden />
            </Button>
          )}
          {canChangeStatus && (
            <Button
              type="button"
              icon
              tooltip="Mark resolved"
              variant="secondary"
              aria-expanded={resolveFormOpen}
              disabled={pendingAction !== null}
              onClick={() => {
                setResolveFormOpen((open) => !open)
              }}
            >
              <CircleCheck aria-hidden />
            </Button>
          )}
          {hasActions && (
            <Button
              type="button"
              icon
              tooltip="Flag wrong"
              variant="secondary"
              aria-expanded={flagFormOpen}
              disabled={pendingAction !== null}
              onClick={() => {
                setFlagFormOpen((open) => !open)
              }}
            >
              <Flag aria-hidden />
            </Button>
          )}
        </div>
      </div>

      {flagFormOpen && (
        <div className="space-y-2 rounded-md border border-coval-border bg-surface-contrast p-3">
          <label
            className="text-sm font-medium text-coval-text"
            htmlFor={`flag-note-${finding.findingId}`}
            id={`flag-note-label-${finding.findingId}`}
          >
            Why is this finding wrong? (optional)
          </label>
          <Textarea
            id={`flag-note-${finding.findingId}`}
            aria-labelledby={`flag-note-label-${finding.findingId}`}
            value={flagNote}
            maxLength={FINDING_NOTE_MAX_CHARS}
            rows={2}
            placeholder="Tell Sofia what it got wrong so it doesn't repeat this."
            onChange={(event) => {
              setFlagNote(event.target.value)
            }}
          />
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="danger"
              loading={pendingAction === "flag_wrong"}
              disabled={pendingAction !== null}
              onClick={() => {
                void runAction("flag_wrong", flagNote.trim() || undefined)
              }}
            >
              Flag as wrong
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={pendingAction !== null}
              onClick={() => {
                setFlagFormOpen(false)
                setFlagNote("")
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {resolveFormOpen && (
        <div className="space-y-2 rounded-md border border-coval-border bg-surface-contrast p-3">
          <label
            className="text-sm font-medium text-coval-text"
            htmlFor={`resolve-note-${finding.findingId}`}
            id={`resolve-note-label-${finding.findingId}`}
          >
            What changed to resolve this? (required)
          </label>
          <Textarea
            id={`resolve-note-${finding.findingId}`}
            aria-labelledby={`resolve-note-label-${finding.findingId}`}
            value={resolveNote}
            maxLength={FINDING_NOTE_MAX_CHARS}
            rows={2}
            placeholder="For example: updated the agent instructions and verified the new behavior."
            onChange={(event) => {
              setResolveNote(event.target.value)
            }}
          />
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="default"
              loading={pendingAction === "resolve"}
              disabled={pendingAction !== null || !resolveNote.trim()}
              onClick={() => {
                void runAction("resolve", resolveNote.trim())
              }}
            >
              Record resolution
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={pendingAction !== null}
              onClick={() => {
                setResolveFormOpen(false)
                setResolveNote("")
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
